import { z } from "zod";
import { Page, Problem, Uuid } from "./common.js";
import { CalendarQuery, CalendarView } from "./calendar.js";
import { ChatQuery, ChatUnread, ChatView, HomeQuery, HomeSummary } from "./chat.js";
import {
  ActivityEvent,
  ActivityQuery,
  Branch,
  HealthResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  MySignature,
  OperatorTenantSelect,
  PlatformLoginSet,
  PlatformLoginState,
  PublicBranding,
  SetPasswordRequest,
  TlsAskQuery,
  TlsAskResponse,
  WebManifest,
  SetPasswordResponse,
  Tenant,
  User,
} from "./identity.js";
import { Patient, PatientHistory, PatientListQuery, PatientSummary } from "./patients.js";
import { AnalyteGroup, AnalyteGroupDetail, AnalyteGroupQuery, AnalyteListQuery, CatalogueBrowseQuery, CatalogueSnapshot, PricedCatalogueItem, SinceQuery, StudyDetail } from "./catalogue.js";
import {
  CashClose,
  CashCloseListQuery,
  CashClosePrintQuery,
  CashCloseQuery,
  CashCloseSummary,
  CashCloseSummaryPrintQuery,
  CashMovement,
  CashMovementConcepts,
  CashMovementConceptsQuery,
  CashMovementListQuery,
  Doctor,
  DoctorListQuery,
  DoctorReport,
  DoctorReportQuery,
  OrderDetail,
  OrderListQuery,
  Order,
  Payment,
  Quote,
  QuoteListQuery,
  QuoteTicket,
  SaleTicket,
  TicketQuery,
} from "./commerce.js";
import {
  DeliveryAttempt,
  LabelsQuery,
  OrderItemResults,
  OrderLabels,
  PendingSample,
  PendingSamplesCount,
  PendingSamplesCountQuery,
  PendingSamplesQuery,
  ReportArtifact,
  ReportPdfQuery,
  ReportPreviewQuery,
  DraftPreviewQuery,
  ReportsListQuery,
  WorklistItem,
  WorklistQuery,
} from "./work.js";
import { CommandEnvelope, CommandReceipt } from "./commands.js";
import { InstructionImageList, InstructionImageListQuery, QuoteWhatsApp } from "./instructions.js";
import { LabLetterhead, LetterheadSampleQuery } from "./letterhead.js";

/**
 * The REST route table. Reads are plain GETs with `{ items, next_cursor }`; the only mutation
 * endpoints are the command bus and the two auth endpoints. Both the API (to register handlers)
 * and the web app (to type its client) read from here, so a path change is a contract change.
 *
 * Path params are written `:name`. Every route below `/v1` except health, login and logout
 * requires a session cookie and runs inside the tenant's RLS context.
 *
 * Routes whose response is `Html` return `text/html` (printable tickets and labels, sized for
 * thermal paper); `Pdf` routes return `application/pdf`.
 */
const Html = z.string();
const Pdf = z.instanceof(Uint8Array);
const Binary = z.instanceof(Uint8Array);

export const routes = {
  health: { method: "GET", path: "/v1/health", response: HealthResponse, auth: false },

  login: { method: "POST", path: "/v1/auth/login", body: LoginRequest, response: LoginResponse, auth: false },
  logout: { method: "POST", path: "/v1/auth/logout", response: z.object({ ok: z.literal(true) }), auth: true },
  /** One-time link from `create-operator` / `reissue-operator-link` (ADR 0017); rate-limited like login. */
  setPassword: { method: "POST", path: "/v1/auth/set-password", body: SetPasswordRequest, response: SetPasswordResponse, auth: false },
  me: { method: "GET", path: "/v1/me", response: MeResponse, auth: true },
  /** The session user's own digitised signature (set with the `signature.set` command). */
  mySignature: { method: "GET", path: "/v1/me/signature", response: MySignature, auth: true },

  /**
   * The current laboratory's logo (tenant setting `logo_data_uri`), decoded: `image/svg+xml`,
   * `image/png` or `image/jpeg`. 404 when the laboratory has none. The staff app's favicon.
   */
  labLogo: { method: "GET", path: "/v1/lab/logo", response: Binary, auth: true },

  /** The laboratory's letterhead (migration 0023): metadata, null when there is none. Every role. */
  labLetterhead: { method: "GET", path: "/v1/lab/letterhead", response: LabLetterhead, auth: true },
  /** The letterhead file as uploaded (`application/pdf`, `image/png` or `image/jpeg`); 404 without one. */
  labLetterheadFile: { method: "GET", path: "/v1/lab/letterhead/file", response: Binary, auth: true },
  /** A synthetic report laid out on the letterhead with the given free zone (Configuración's live preview). Administrador. */
  labLetterheadSample: { method: "GET", path: "/v1/lab/letterhead/sample.pdf", query: LetterheadSampleQuery, response: Pdf, auth: true },

  /**
   * Public, per host (ADR 0025): what the login screen and the installed icon show before anyone
   * signs in. The laboratory is the one the Host names; nothing else is revealed.
   */
  publicBranding: { method: "GET", path: "/v1/public/branding", response: PublicBranding, auth: false },
  /** The host's laboratory logo (`image/svg+xml`, `image/png` or `image/jpeg`); 404 on the platform host or without one. */
  publicLogo: { method: "GET", path: "/v1/public/logo", response: Binary, auth: false },
  /** The web app manifest, per host: the lab's name and logo on its own host, Probeta Lab elsewhere. */
  publicManifest: { method: "GET", path: "/v1/public/manifest.webmanifest", response: WebManifest, auth: false },
  /** Caddy's on-demand TLS check. Loopback only, never proxied: 200 allows a certificate, 404 refuses. */
  tlsAsk: { method: "GET", path: "/v1/tls/ask", query: TlsAskQuery, response: TlsAskResponse, auth: false },

  /** Platform operators only (ADR 0017): the laboratories they may enter, and entering one. */
  operatorTenants: { method: "GET", path: "/v1/operator/tenants", response: Page(Tenant), auth: true },
  operatorTenant: { method: "POST", path: "/v1/operator/tenant", body: OperatorTenantSelect, response: LoginResponse, auth: true },
  /** Operators only (ADR 0025): may the current laboratory's staff sign in on the platform host? */
  operatorPlatformLogin: { method: "POST", path: "/v1/operator/platform-login", body: PlatformLoginSet, response: PlatformLoginState, auth: true },

  commands: { method: "POST", path: "/v1/commands", body: CommandEnvelope, response: CommandReceipt, auth: true },
  commandReceipt: { method: "GET", path: "/v1/commands/:id", response: CommandReceipt, auth: true },

  branches: { method: "GET", path: "/v1/branches", response: Page(Branch), auth: true },
  users: { method: "GET", path: "/v1/users", response: Page(User), auth: true },
  activity: { method: "GET", path: "/v1/activity", query: ActivityQuery, response: Page(ActivityEvent), auth: true },

  patients: { method: "GET", path: "/v1/patients", query: PatientListQuery, response: Page(PatientSummary), auth: true },
  patient: { method: "GET", path: "/v1/patients/:id", response: Patient, auth: true },
  patientHistory: { method: "GET", path: "/v1/patients/:id/history", response: PatientHistory, auth: true },

  catalogue: { method: "GET", path: "/v1/catalogue", query: SinceQuery, response: CatalogueSnapshot, auth: true },
  catalogueBrowse: {
    method: "GET",
    path: "/v1/catalogue/browse",
    query: CatalogueBrowseQuery,
    response: Page(PricedCatalogueItem),
    auth: true,
  },
  study: { method: "GET", path: "/v1/catalogue/studies/:id", response: StudyDetail, auth: true },
  /** Catálogo → Analitos: active analytes grouped by clave, with the studies that carry each one. */
  catalogueAnalytes: { method: "GET", path: "/v1/catalogue/analytes", query: AnalyteListQuery, response: Page(AnalyteGroup), auth: true },
  catalogueAnalyteGroup: { method: "GET", path: "/v1/catalogue/analytes/group", query: AnalyteGroupQuery, response: AnalyteGroupDetail, auth: true },

  doctors: { method: "GET", path: "/v1/doctors", query: DoctorListQuery, response: Page(Doctor), auth: true },

  quotes: { method: "GET", path: "/v1/quotes", query: QuoteListQuery, response: Page(Quote), auth: true },
  quote: { method: "GET", path: "/v1/quotes/:id", response: Quote, auth: true },
  quoteTicket: { method: "GET", path: "/v1/quotes/:id/ticket", response: QuoteTicket, auth: true },
  quoteTicketHtml: { method: "GET", path: "/v1/quotes/:id/ticket.html", query: TicketQuery, response: Html, auth: true },
  /** What the WhatsApp hand-off composes its message from (names, preparation, turnaround, phone). */
  quoteWhatsApp: { method: "GET", path: "/v1/quotes/:id/whatsapp", response: QuoteWhatsApp, auth: true },

  /** The lab's patient instruction images (metadata only); active ones unless `include_inactive`. */
  instructionImages: { method: "GET", path: "/v1/instruction-images", query: InstructionImageListQuery, response: InstructionImageList, auth: true },
  /** Returns the image bytes (`image/png` or `image/jpeg`). */
  instructionImageFile: { method: "GET", path: "/v1/instruction-images/:id/file", response: Binary, auth: true },

  orders: { method: "GET", path: "/v1/orders", query: OrderListQuery, response: Page(Order), auth: true },
  order: { method: "GET", path: "/v1/orders/:id", response: OrderDetail, auth: true },
  orderPayments: { method: "GET", path: "/v1/orders/:id/payments", response: Page(Payment), auth: true },
  orderTicket: { method: "GET", path: "/v1/orders/:id/ticket", response: SaleTicket, auth: true },
  orderTicketHtml: { method: "GET", path: "/v1/orders/:id/ticket.html", query: TicketQuery, response: Html, auth: true },
  orderLabels: { method: "GET", path: "/v1/orders/:id/labels", response: OrderLabels, auth: true },
  /** 50 × 25 mm labels, one page per tube, Code128 of the label code. */
  orderLabelsHtml: { method: "GET", path: "/v1/orders/:id/labels.html", query: LabelsQuery, response: Html, auth: true },
  orderReports: { method: "GET", path: "/v1/orders/:id/reports", query: ReportsListQuery, response: Page(ReportArtifact), auth: true },
  /** Draft PDF, never stored, watermarked on unvalidated items. */
  orderPreviewPdf: { method: "GET", path: "/v1/orders/:id/preview.pdf", query: ReportPreviewQuery, response: Pdf, auth: true },

  /** Captura's «Vista previa»: saved values, watermarked, unsigned, never stored or deliverable. Staff. */
  orderDraftPreviewPdf: { method: "GET", path: "/v1/orders/:id/draft-preview.pdf", query: DraftPreviewQuery, response: Pdf, auth: true },

  worklist: { method: "GET", path: "/v1/worklist", query: WorklistQuery, response: Page(WorklistItem), auth: true },
  orderItemResults: { method: "GET", path: "/v1/order-items/:id/results", response: OrderItemResults, auth: true },
  /** A study's attached document as uploaded (`application/pdf`, `image/jpeg` or `image/png`); staff only, chained as an access event (ADR 0030). */
  resultAttachmentFile: { method: "GET", path: "/v1/result-attachments/:id/file", response: Binary, auth: true },

  /** Muestras pendientes: tubes still to be collected (pending, or rejected and awaiting a new collection). */
  pendingSamples: { method: "GET", path: "/v1/samples/pending", query: PendingSamplesQuery, response: Page(PendingSample), auth: true },
  /** The sidebar badge: how many of those tubes, in one query. */
  pendingSamplesCount: { method: "GET", path: "/v1/samples/pending/count", query: PendingSamplesCountQuery, response: PendingSamplesCount, auth: true },

  /** Returns `application/pdf`. Every download is written to the audit chain as an access event. */
  reportPdf: { method: "GET", path: "/v1/reports/:id/pdf", query: ReportPdfQuery, response: Pdf, auth: true },
  reportDeliveries: { method: "GET", path: "/v1/reports/:id/deliveries", response: Page(DeliveryAttempt), auth: true },

  cashCloseSummary: { method: "GET", path: "/v1/cash-close/summary", query: CashCloseQuery, response: CashCloseSummary, auth: true },
  cashCloses: { method: "GET", path: "/v1/cash-close", query: CashCloseListQuery, response: Page(CashClose), auth: true },
  /** Extra income / expense of a drawer in a period (voided ones flagged), and earlier concepts to suggest. */
  cashMovements: { method: "GET", path: "/v1/cash-movements", query: CashMovementListQuery, response: z.object({ items: z.array(CashMovement) }), auth: true },
  cashMovementConcepts: { method: "GET", path: "/v1/cash-movements/concepts", query: CashMovementConceptsQuery, response: CashMovementConcepts, auth: true },
  /** Printable cash close (thermal 80/58 mm or letter): the live drawer, and a recorded close by id. */
  cashCloseSummaryHtml: { method: "GET", path: "/v1/cash-close/summary.html", query: CashCloseSummaryPrintQuery, response: Html, auth: true },
  cashCloseHtml: { method: "GET", path: "/v1/cash-close/:id/print.html", query: CashClosePrintQuery, response: Html, auth: true },

  /** Calendario: studies due per day, the overdue ones, and the staff's own entries (ADR 0027). */
  calendar: { method: "GET", path: "/v1/calendar", query: CalendarQuery, response: CalendarView, auth: true },

  /** Inicio: today's numbers at a branch (ADR 0028). */
  home: { method: "GET", path: "/v1/home", query: HomeQuery, response: HomeSummary, auth: true },
  /** Chat entre sucursales: the latest messages and the user's unread count (ADR 0028). */
  chat: { method: "GET", path: "/v1/chat", query: ChatQuery, response: ChatView, auth: true },
  /** The chat bubble's badge, polled while the tab is visible. */
  chatUnread: { method: "GET", path: "/v1/chat/unread", response: ChatUnread, auth: true },

  doctorReport: { method: "GET", path: "/v1/reports/doctors", query: DoctorReportQuery, response: DoctorReport, auth: true },
} as const;

export type Routes = typeof routes;
export type RouteName = keyof Routes;

/** Replace `:param` segments. */
export function routePath(path: string, params: Record<string, string> = {}): string {
  return path.replace(/:([a-zA-Z_]+)/g, (_, name: string) => {
    const v = params[name];
    if (v === undefined) throw new Error(`missing route param ${name}`);
    return encodeURIComponent(v);
  });
}

export const ErrorResponse = Problem;
export const IdParam = z.object({ id: Uuid });
